import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import { motion } from 'framer-motion';

const PrivacyPolicy: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ minHeight: '100vh', backgroundColor: '#0a0e27', paddingTop: '2rem', paddingBottom: '4rem' }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#14192d' }}>
          <Typography variant="h3" gutterBottom sx={{ color: '#0066FF', fontWeight: 'bold', mb: 3 }}>
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#888', mb: 4 }}>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                1. Introduction
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                Reachout AI Corporation ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                2. Information We Collect
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                We collect information that you provide directly to us, including:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Name, email address, and phone number</li>
                <li>Business information and account credentials</li>
                <li>Payment and billing information</li>
                <li>Communications and messages sent through our platform</li>
                <li>Customer interaction data and preferences</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                3. How We Use Your Information
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                We use the information we collect to:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send administrative information, updates, and security alerts</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues and fraudulent activity</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                4. SMS and Mobile Information
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                Our platform enables SMS messaging functionality for business communications. Regarding your mobile information:
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#0066FF', 
                  lineHeight: 1.8, 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0, 102, 255, 0.1)',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 102, 255, 0.3)'
                }}
              >
                No mobile information will be shared with third parties for marketing/promotional purposes. This excludes text messaging originator opt-in data and consent, which will not be shared with any third parties.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                5. Information Sharing and Disclosure
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                We may share your information in the following circumstances:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>With your consent or at your direction</li>
                <li>With service providers who perform services on our behalf</li>
                <li>To comply with legal obligations or respond to lawful requests</li>
                <li>To protect the rights, property, and safety of Reachout AI, our users, or others</li>
                <li>In connection with a merger, sale, or acquisition of all or part of our business</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                6. Data Security
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                7. Data Retention
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                8. Your Rights and Choices
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                You have the right to:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Access, update, or delete your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data</li>
                <li>Object to processing of your personal information</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                10. Changes to This Privacy Policy
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                11. Contact Us
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                If you have any questions about this Privacy Policy, please contact us at:
              </Typography>
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 102, 255, 0.05)', borderRadius: '8px' }}>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Reachout AI Corporation
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc' }}>
                  1500 N GRANT ST # 7298
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc' }}>
                  DENVER, CO 80203
                </Typography>
                <Typography variant="body1" sx={{ color: '#0066FF', mt: 1 }}>
                  support@reachoutaiapp.com
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </motion.div>
  );
};

export default PrivacyPolicy;
